import type { ApiErrorBody, TokenResponse, UserDto } from '@bnn/shared';
import { getDeviceId, kv } from './kv';

const BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`;

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let onAuthLost: (() => void) | null = null;
let refreshing: Promise<UserDto | null> | null = null;

export function setAuthLostHandler(fn: () => void) {
  onAuthLost = fn;
}

export async function applyTokens(t: TokenResponse): Promise<UserDto> {
  accessToken = t.access_token;
  await kv.set('refresh_token', t.refresh_token);
  return t.user;
}

export async function clearTokens() {
  accessToken = null;
  await kv.del('refresh_token');
}

export const getRefreshToken = () => kv.get<string>('refresh_token');

/**
 * Exchange the refresh token for a new pair. Single-flight within the tab, and serialised
 * across tabs with the Web Locks API so two tabs never present the same (rotating) token.
 */
export function refreshSession(): Promise<UserDto | null> {
  refreshing ??= (async () => {
    const run = async (): Promise<UserDto | null> => {
      const rt = await getRefreshToken();
      if (!rt) return null;
      let res: Response;
      try {
        res = await fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: rt, device_id: await getDeviceId() }),
        });
      } catch {
        throw new ApiError(0, 'NETWORK', 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ตรวจสอบอินเทอร์เน็ต');
      }
      // Only a rejected token ends the session. A gateway error or a server hiccup must not log
      // counters out in the middle of a count: treat it like being offline and keep the token.
      if (res.status >= 500) throw new ApiError(res.status, 'NETWORK', 'เซิร์ฟเวอร์ไม่พร้อมใช้งานชั่วคราว');
      if (!res.ok) {
        await clearTokens();
        return null;
      }
      return applyTokens((await res.json()) as TokenResponse);
    };
    return navigator.locks ? navigator.locks.request('bnn-refresh', run) : run();
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function send(method: string, path: string, body?: unknown, isForm = false): Promise<Response> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  try {
    return await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ตรวจสอบอินเทอร์เน็ต');
  }
}

async function toError(res: Response): Promise<ApiError> {
  let b: Partial<ApiErrorBody> = {};
  try {
    b = (await res.json()) as ApiErrorBody;
  } catch {
    /* non-JSON */
  }
  // 502/503/504, or a 5xx that is not our API's JSON error, means the API itself is unreachable
  // (proxy / platform restarting): handle it exactly like having no network.
  if ((res.status >= 502 && res.status <= 504) || (res.status >= 500 && !b.code)) {
    return new ApiError(res.status, 'NETWORK', 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ชั่วคราว');
  }
  return new ApiError(res.status, b.code ?? 'HTTP_' + res.status, b.message ?? `เกิดข้อผิดพลาด (${res.status})`, b.details);
}

/** Call the API with the access token; on 401 refresh once and retry. */
async function call(method: string, path: string, body?: unknown, isForm = false): Promise<Response> {
  let res = await send(method, path, body, isForm);
  if (res.status === 401 && !path.startsWith('/auth/login')) {
    const user = await refreshSession();
    if (!user) {
      onAuthLost?.();
      throw await toError(res);
    }
    res = await send(method, path, body, isForm);
  }
  if (!res.ok) throw await toError(res);
  return res;
}

export const api = {
  async get<T>(path: string): Promise<T> {
    return (await call('GET', path)).json() as Promise<T>;
  },
  async post<T>(path: string, body?: unknown): Promise<T> {
    const res = await call('POST', path, body ?? {});
    return (res.status === 204 ? undefined : await res.json()) as T;
  },
  async patch<T>(path: string, body: unknown): Promise<T> {
    return (await call('PATCH', path, body)).json() as Promise<T>;
  },
  async upload<T>(path: string, file: File): Promise<T> {
    const fd = new FormData();
    fd.append('file', file);
    return (await call('POST', path, fd, true)).json() as Promise<T>;
  },
  /** Download a file (e.g. Excel template) with auth and save it. */
  async download(path: string, fallbackName: string): Promise<void> {
    const res = await call('GET', path);
    const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 1000);
  },
};

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}
